/**
 * WHAT AN EMPTY DASHBOARD MEANS.
 *
 * On 2026-09-20 an account that owned thirty studies was shown "No projects
 * yet — get started by creating your first clinical research project". The
 * studies were fine; the account's sign-in token had no organisation, so the
 * server (correctly) returned nothing. The screen turned a permission problem
 * into a statement that the data did not exist, and the reasonable conclusion
 * was that a database had been deleted.
 *
 * So the Dashboard asks what its empty state MEANS before rendering one.
 * Pure and DOM-free: `web` has no test runner and must not gain one, so its
 * testable logic lives in functions like this and is exercised from there.
 */

export interface ListMyProjectsResult {
  projects?: unknown[];
  /** Set by listMyProjects when the caller's token carries no orgId. */
  orgMissing?: boolean;
}

export interface DashboardEmptyState {
  kind: 'has-projects' | 'no-projects' | 'no-organisation';
  title: string;
  body: string;
  /** Whether to offer "Create Project". Never offered when the list is untrustworthy. */
  showCreateCta: boolean;
}

export function dashboardEmptyState(res: ListMyProjectsResult | null | undefined): DashboardEmptyState {
  const projects = Array.isArray(res?.projects) ? res!.projects : [];

  // A non-empty list is evidence the caller CAN see, whatever a stale flag
  // says. Never hide real studies behind a warning.
  if (projects.length > 0) {
    return { kind: 'has-projects', title: '', body: '', showCreateCta: true };
  }

  if (res?.orgMissing === true) {
    return {
      kind: 'no-organisation',
      title: 'Your account is not attached to an organisation',
      body:
        'Any studies you already have are safe — this account simply cannot ' +
        'see them yet. Try signing out and signing in again, which refreshes ' +
        'your access. If the screen is still empty, ask an administrator to ' +
        'add your account to your organisation.',
      // Creating here would make a duplicate of a study they already own.
      showCreateCta: false,
    };
  }

  return {
    kind: 'no-projects',
    title: 'No projects yet',
    body: 'Get started by creating your first clinical research project',
    showCreateCta: true,
  };
}
